import {FC, useEffect} from 'react'
import {Controller, useFormContext, useWatch} from 'react-hook-form'
import {useDispatch} from 'react-redux'
import {yup} from 'libs/yup'
import {gql, isApolloError, useApolloClient} from '@apollo/client'
import {get, at} from 'lodash'
import classNames from 'classnames'
import {Checkbox} from 'jbc-front/components/presenters/form/Checkbox'
import {Input} from 'jbc-front/components/presenters/form/Input'
import {Select} from 'jbc-front/components/presenters/form/Select'
import {ErrorMessage} from 'jbc-front/components/presenters/form/ErrorMessage'
import {Body, Head, Panel} from 'components/ui/Panel'
import {Label} from 'components/forms/Label'
import {DateField} from 'components/reactHookForm/DateField'
import {prefectureOptionsWithEmpty} from 'consts/prefectures'
import {dateFormat} from 'libs/formatter'
import {handleQueryError} from 'libs/graphql'
import styles from './LaborConsultant.scss'

export const laborConsultantSchema = yup.object({
  needLaborConsultant: yup.boolean().notRequired().label('社労士が代行して作成'),
  laborConsultantCreateDate: yup.string().notRequired().dateFormat().label('作成年月日'),
  laborConsultantName: yup.string().notRequired().max(30).label('社労士名'),
  laborConsultantAgentType: yup.string().notRequired().oneOf(['submission_agent', 'office_agent']).label('代理区分'),
  laborConsultantPrefectureId: yup.string().notRequired().label('社会保険労務士都道府県会'),
  laborConsultantPhoneNumber0: yup
    .string()
    .notRequired()
    .emptyToNull()
    .numeric()
    .min(2)
    .max(4)
    .label('電話番号の市外局番'),
  laborConsultantPhoneNumber1: yup
    .string()
    .notRequired()
    .emptyToNull()
    .numeric()
    .min(1)
    .max(4)
    .label('電話番号の市内局番'),
  laborConsultantPhoneNumber2: yup
    .string()
    .notRequired()
    .emptyToNull()
    .numeric()
    .min(3)
    .max(4)
    .label('電話番号の加入者番号'),
})

export type LeborConsultantSchema = yup.InferType<typeof laborConsultantSchema>

const GET_LABOR_CONSULTANT_DATA = gql`
  query GetLaborConsultantData($procedureStatusId: Int!, $reportName: String!) {
    getLaborConsultantData(procedureStatusId: $procedureStatusId, reportName: $reportName) {
      id
      name
      agentType
      prefectureId
      phoneNumber0
      phoneNumber1
      phoneNumber2
      currentDate
    }
  }
`

interface GetLaborConsultantDataQueryVariables {
  procedureStatusId: number
  reportName: string
}

interface GetLaborConsultantDataQueryResult {
  getLaborConsultantData?: {
    id: string
    name: string
    agentType: 'submission_agent' | 'office_agent'
    prefectureId: number
    phoneNumber0: string
    phoneNumber1: string
    phoneNumber2: string
    currentDate: string
  }
}

const agentTypes = [
  {value: '', label: ''},
  {value: 'submission_agent', label: '提出代行者'},
  {value: 'office_agent', label: '事務代理者'},
]

interface LaborConsultantSectionProps {
  className?: string
  procedureStatusId: string
  reportName: string
  prefix?: string
}

export const LaborConsultantSection: FC<LaborConsultantSectionProps> = ({
  className,
  procedureStatusId,
  reportName,
  prefix,
}) => {
  const apolloClient = useApolloClient()
  const dispatch = useDispatch()
  const {
    register,
    setValue,
    control,
    formState: {errors},
  } = useFormContext()
  const needLaborConsultant: boolean | undefined = useWatch({control, name: `${prefix}needLaborConsultant`})

  useEffect(() => {
    if (needLaborConsultant) {
      const procId = parseInt(procedureStatusId, 10)
      if (Number.isNaN(procId)) return

      apolloClient
        .query<GetLaborConsultantDataQueryResult, GetLaborConsultantDataQueryVariables>({
          query: GET_LABOR_CONSULTANT_DATA,
          variables: {
            procedureStatusId: procId,
            reportName,
          },
          fetchPolicy: 'no-cache',
        })
        .then((res) => {
          const data = res.data.getLaborConsultantData
          if (!data) return

          setValue(
            `${prefix}laborConsultantCreateDate`,
            data.currentDate ? dateFormat(data.currentDate, 'YYYY/MM/DD') : ''
          )
          setValue(`${prefix}laborConsultantName`, data.name)
          setValue(`${prefix}laborConsultantAgentType`, data.agentType)
          setValue(`${prefix}laborConsultantPrefectureId`, String(data.prefectureId))
          setValue(`${prefix}laborConsultantPhoneNumber0`, data.phoneNumber0)
          setValue(`${prefix}laborConsultantPhoneNumber1`, data.phoneNumber1)
          setValue(`${prefix}laborConsultantPhoneNumber2`, data.phoneNumber2)
        })
        .catch((err) => isApolloError(err) && handleQueryError(err, dispatch))
    } else {
      setValue(`${prefix}laborConsultantCreateDate`, '')
      setValue(`${prefix}laborConsultantName`, '')
      setValue(`${prefix}laborConsultantAgentType`, undefined)
      setValue(`${prefix}laborConsultantPrefectureId`, undefined)
      setValue(`${prefix}laborConsultantPhoneNumber0`, '')
      setValue(`${prefix}laborConsultantPhoneNumber1`, '')
      setValue(`${prefix}laborConsultantPhoneNumber2`, '')
    }
  }, [procedureStatusId, reportName, prefix, apolloClient, dispatch, setValue, needLaborConsultant])

  const phoneNumberErrors = (
    at(errors, [
      `${prefix}laborConsultantPhoneNumber0.message`,
      `${prefix}laborConsultantPhoneNumber1.message`,
      `${prefix}laborConsultantPhoneNumber2.message`,
    ]) as (string | undefined)[]
  ).filter((v) => v !== undefined)
  const laborConsultantNameError = get(errors, `${prefix}laborConsultantName.message`)

  return (
    <Panel className={classNames(styles.panel, className)}>
      <Head>社会保険労務士記載欄</Head>
      <Body className={styles.formBody}>
        <div className={styles.needLaborConsultantField}>
          <Checkbox {...register(`${prefix}needLaborConsultant`)}>社労士が代行して作成</Checkbox>
        </div>
        <div className={styles.field}>
          <Label>作成年月日</Label>
          <DateField
            name={`${prefix}laborConsultantCreateDate`}
            error={get(errors, `${prefix}laborConsultantCreateDate.message`) as string | undefined}
            disabled={!needLaborConsultant}
          />
        </div>
        <div className={styles.field}>
          <Label>代理区分</Label>
          <Controller
            control={control}
            name={`${prefix}laborConsultantAgentType`}
            render={({field: {onChange, onBlur, value}}) => (
              <Select
                options={agentTypes}
                onChange={({value}) => onChange(value)}
                onBlur={onBlur}
                value={agentTypes.find((option) => option.value === value)}
                disabled={!needLaborConsultant}
                placeholder="選択してください"
              />
            )}
          />
        </div>
        <div className={styles.field}>
          <Label>社会保険労務士都道府県会</Label>
          <Controller
            control={control}
            name={`${prefix}laborConsultantPrefectureId`}
            render={({field: {onChange, onBlur, value}}) => (
              <Select
                options={prefectureOptionsWithEmpty}
                onChange={({value}) => onChange(value)}
                onBlur={onBlur}
                value={prefectureOptionsWithEmpty.find((option) => option.value === value)}
                disabled={!needLaborConsultant}
                placeholder="選択してください"
              />
            )}
          />
        </div>
        <div className={styles.field}>
          <Label>社労士名</Label>
          <Input
            {...register(`${prefix}laborConsultantName`)}
            isError={!!get(errors, `${prefix}laborConsultantName`)}
            disabled={!needLaborConsultant}
          />
          {typeof laborConsultantNameError === 'string' && <ErrorMessage>{laborConsultantNameError}</ErrorMessage>}
        </div>
        <div className={styles.field}>
          <Label>電話番号</Label>
          <div className={styles.phoneNumber}>
            <Input
              className={styles.phoneNumberSegment}
              {...register(`${prefix}laborConsultantPhoneNumber0`)}
              maxLength={4}
              isError={!!get(errors, `${prefix}laborConsultantPhoneNumber0`)}
              disabled={!needLaborConsultant}
            />
            -
            <Input
              className={styles.phoneNumberSegment}
              {...register(`${prefix}laborConsultantPhoneNumber1`)}
              maxLength={4}
              isError={!!get(errors, `${prefix}laborConsultantPhoneNumber1`)}
              disabled={!needLaborConsultant}
            />
            -
            <Input
              className={styles.phoneNumberSegment}
              {...register(`${prefix}laborConsultantPhoneNumber2`)}
              maxLength={4}
              isError={!!get(errors, `${prefix}laborConsultantPhoneNumber2`)}
              disabled={!needLaborConsultant}
            />
          </div>
          {phoneNumberErrors.length > 0 && (
            <div>
              {phoneNumberErrors.map((error, index) => (
                <ErrorMessage key={index}>{error}</ErrorMessage>
              ))}
            </div>
          )}
        </div>
      </Body>
    </Panel>
  )
}
